import { useEffect, useRef, useState } from "react";
import {
  EmptyState,
  IconGroup,
  ImagePreview,
  SectionBox,
  SectionTitle,
  StyledInput,
  IconButton,
  StyledTextArea,
  InlineToast,
} from "./EditableSectionStyles";

import PencilIcon from "../../img/logged/edit.png";
import TrashIcon from "../../img/logged/trash.png";
import EditSectionModal from "../editSectionModal/EditSectionModal";
import { EditableSectionProps } from "../../types/componentsConstructors";

const EditableSection = ({
  type,
  title,
  value,
  onChange,
}: EditableSectionProps) => {
  const [showModal, setShowModal] = useState(false);

  const [toast, setToast] = useState<string | null>(null);
  const toastTimerRef = useRef<number | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = window.setTimeout(() => setToast(null), 2400);
  };

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  const isEmpty =
    (typeof value === "string" && !value.trim()) ||
    (Array.isArray(value) && value.length === 0);

  const isEditableModalType = (
    t: string
  ): t is "text" | "textarea" | "link" | "multiLink" => {
    return ["text", "textarea", "link", "multiLink"].includes(t);
  };

  const handleModalType = (): "text" | "textarea" | "link" | "multiLink" => {
    if (title === "Música e set" || title === "Eventos") return "multiLink";
    if (isEditableModalType(type)) return type;
    return "text";
  };

  const modalType = handleModalType();

  const openModal = (e?: React.MouseEvent | React.FocusEvent) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setShowModal(true);
  };

  const renderDisplay = () => {
    if (type === "text" && typeof value === "string") {
      return (
        <StyledInput
          type="text"
          value={value}
          placeholder="Fale sobre você como artista..."
          readOnly
          onMouseDown={openModal}
          onFocus={openModal}
        />
      );
    }

    if (type === "textarea" && typeof value === "string") {
      return (
        <StyledTextArea
          value={value}
          placeholder="Crie uma frase que te represente. Exemplo: 'Criando experiências únicas através da música.'"
          readOnly
          onMouseDown={openModal}
          onFocus={openModal}
        />
      );
    }

    if (type === "link" && typeof value === "string") {
      return (
        <StyledInput
          type="url"
          value={value}
          placeholder="https://www.link.com"
          readOnly
          onMouseDown={openModal}
          onFocus={openModal}
        />
      );
    }

    if (type === "image" && typeof value === "string") {
      return value ? (
        <ImagePreview
          src={value}
          alt={`preview-${title}`}
          onClick={openModal}
        />
      ) : (
        <EmptyState onClick={openModal}>Sem imagem enviada</EmptyState>
      );
    }

    if (
      (title === "Música e set" || title === "Eventos") &&
      Array.isArray(value)
    ) {
      const sanitized = value.filter(
        (i) => i && i.name?.trim() !== "" && i.url?.trim() !== ""
      );

      return sanitized.length === 0 ? (
        <EmptyState onClick={openModal}>Vazio</EmptyState>
      ) : (
        <div style={{ color: "#fff", fontSize: 14 }} onClick={openModal}>
          {sanitized.map((item, index) => (
            <div key={index} style={{ marginBottom: 8 }}>
              <strong>{item.name}</strong>
              <br />
              <span style={{ fontSize: 12, color: "#999" }}>{item.url}</span>
            </div>
          ))}
        </div>
      );
    }

    return null;
  };

  const handleReset = () => {
    if (modalType === "multiLink") {
      onChange?.([]);
    } else {
      onChange?.("");
    }
  };

  return (
    <>
      <SectionBox $empty={isEmpty} onClick={openModal}>
        <SectionTitle>{title}</SectionTitle>

        {renderDisplay()}

        <IconGroup onClick={(e) => e.stopPropagation()}>
          <IconButton onClick={openModal}>
            <img src={PencilIcon} alt="Editar" />
          </IconButton>
          <IconButton onClick={handleReset}>
            <img src={TrashIcon} alt="Apagar" />
          </IconButton>
        </IconGroup>
      </SectionBox>

      {showModal && (
        <EditSectionModal
          title={title}
          type={modalType}
          value={value}
          onClose={() => setShowModal(false)}
          onDismissEmpty={() =>
            showToast("Nada salvo, pois nenhum item foi preenchido.")
          }
          onSave={(newVal) => {
            onChange?.(newVal);
            setShowModal(false);
          }}
        />
      )}

      {toast && <InlineToast>{toast}</InlineToast>}
    </>
  );
};

export default EditableSection;
